import React, {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import DataGrid, {Button, Column, DataGridRef, Editing, Lookup, Scrolling, Selection} from "devextreme-react/data-grid";
import IReservationDetail from "../../models/IReservationDetail";
import useApi, {IApiErreur} from "../../hooks/useApi";
import IReservationDetailSommaire, {ReservationDetailSommaireTypeAuxiliaire} from "../../models/IReservationDetailSommaire";
import IGestionReservation from "../../models/IGestionReservation";
import useLookUpBase from "../../components/selectbox/LookupBase";
import GestionReservaitonTrancheAgePopup from "./gestion-reservaiton-tranche-age-popup";
import IReservationAge from "../../models/IReservationAge";
import {InitNewRowEvent} from "devextreme/ui/data_grid";
import CustomStore from "devextreme/data/custom_store";
import {useAuth} from "../../contexts/auth";
import {ICustomRule} from "../../hooks/useCustomDataSource";
import {useStorage} from "../../hooks/useStorage";
import {ReservationDetailTypeDetail} from "../../models/ReservationDetailTypeDetail";

export interface IReservationDetailViewProps {
    selectedReservation: IGestionReservation,
    data: any,
    isModeModifier: boolean,
    dataGridStore: CustomStore
}

function ReservationDetailView({data, selectedReservation, isModeModifier, dataGridStore}: IReservationDetailViewProps) {
    const {user} = useAuth();
    const {reservationDetails, typeAuxiliaire, idReservationDetailSommaire} = data.data;
    const {editorOptions: planEditorOptions} = useLookUpBase({controllerName: "planentete",})
    const {postData: postReservationDetail, data: reservationDetail} = useApi<{ reservationDetail: IReservationDetail, reservationAges: IReservationAge[] }>();
    const dataGridRef = useRef<DataGridRef>(null);
    const visibleSiUnite = useMemo(() => typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Unite, [typeAuxiliaire]);
    const totalWidth: number | string | undefined = visibleSiUnite ? "10%" : undefined;
    const {fetchData} = useApi("ReservationAge")
    const [validationRules, setValidationRules, validationRulesIsExpired] = useStorage<ICustomRule[]>({key: "validationRules-ReservationDetail", defaultValue: [], expirationInMilliseconds: 300000, storageType: "localStorage"});
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        const abortController = new AbortController();
        if (validationRulesIsExpired || validationRules.length == 0) {
            fetchData("get", {customPath: `reservationDetail/getValidation`, returnOnly: true, abortSignal: abortController.signal}).then((result: string[]) => {
                if (result) {
                    setValidationRules(result.map(fieldName => {
                        return {type: "async", message: "", reevaluate: true, propertyName: fieldName} as ICustomRule
                    }))
                    dataGridRef.current?.instance().refresh()
                }
            })
        }

    }, [dataGridRef, validationRulesIsExpired])

    const validate = useCallback(async (data: IReservationDetailSommaire | undefined) => {
        setHasError(false)
        const result = await fetchData("post", {
            customPath: `reservationDetail/ValidateModel`,
            body: data as unknown as object,
            returnOnly: true
        });
        return result as unknown[] as IApiErreur[];
    }, [fetchData]);

    const validationCallback = useCallback((e: any) => {
        return new Promise(async (resolve, reject) => {
            if (!isModeModifier) return resolve(undefined);
            try {

                const result: IApiErreur[] = await validate(e.data);
                const erreur = result.find(f => f.propertyName.toLowerCase() === e.rule.propertyName.toLowerCase())
                if (erreur) {
                    setHasError(true)
                    reject(erreur.errorMessage);
                }
            } catch (ex) {
                resolve(undefined);
            }
            resolve(undefined);
        })
    }, [isModeModifier, validate])

    const customizeColumns = useCallback((items: any) => {
        if (!isModeModifier || !validationRules || validationRules.length === 0) return;

        items.forEach((col: any) => {
            if (col.dataField) {
                const rule = validationRules.find(f => f.propertyName.toLowerCase() === col.dataField?.toLowerCase());
                if (rule) {
                    rule.validationCallback = validationCallback;
                    col.validationRules = [rule];
                }
            }
        });

    }, [validationRules, isModeModifier, validationCallback])

    function isValidationErreur(arg: any[]): arg is IApiErreur[] {
        const result = arg && arg.length > 0 && arg[0] && arg[0].propertyName && typeof (arg[0].propertyName) === 'string'
        return result;
    }

    const trancheDetailAgeCellRender = useCallback(({data, adulte}: { data: IReservationDetail, adulte: boolean }) => {
        const {idReservationDetail, reservationAges} = data;

        let trancheAges = reservationAges.filter(f => f.isAdulte === adulte);
        if (trancheAges?.length == 0) {
            trancheAges = reservationAges.filter(f => f.isAdulte === adulte);
        }

        return <GestionReservaitonTrancheAgePopup id={`detail-${idReservationDetail}`} adulte={adulte} border={false} trancheAges={trancheAges} isModeModifier={isModeModifier && data.type !== ReservationDetailTypeDetail.Automatique} onTrancheAgeValueChanged={() => {
            if (isModeModifier && data.type !== ReservationDetailTypeDetail.Automatique && data.type !== ReservationDetailTypeDetail.Modifier)
                dataGridStore.update(idReservationDetailSommaire, {detailOnly: true})
        }}/>
    }, [isModeModifier, dataGridStore, idReservationDetailSommaire])

    function getCalculateCellValue(e: IReservationDetail) {
        return e.prixParJourAdultes * e.quantite;
    }

    const onInitNewRow = useCallback((e: InitNewRowEvent) => {
        e.promise = postReservationDetail({
            ReservationDetailSommaire: data.data,
            IdUsager: user?.idUsager
        }, 'reservationDetailSommaire/CreateNewRowDetail').then(
            (newRow) => {
                e.data = newRow.reservationDetail
            }
        );
    }, [postReservationDetail, data.data, user?.idUsager]);

    //const gridDataSource = useMemo(() => new DataSource(new CustomStore({key: "idReservationDetail", load: () => reservationDetails})), [reservationDetails]);

    const onRowInserted = useCallback((e: any) => {
        dataGridStore.update(e.data.idReservationDetailSommaire, {reservationDetailSommaire: data.data, detailOnly: true});
    }, [dataGridStore, data.data]);
    const onRowUpdated = useCallback((e: any) => {
            dataGridStore.update(e.data.idReservationDetailSommaire, {
                reservationDetailSommaire: {...data.data, reservationDetails: data.data.reservationDetails.map((d: IReservationDetail) => ( !d.idReservationDetail.equals(e.data.idReservationDetail) || d.type !== ReservationDetailTypeDetail.Automatique ? d : {...d, type: ReservationDetailTypeDetail.Modifier}))},
                detailOnly: true
            })
        }, [dataGridStore, data.data]
    );
    const onEditorPreparing = useCallback((e: any) => {
        if (e.parentType === "dataRow") {
            if (e.dataField === "dateDebut" || e.dataField === "dateFin" || e.dataField === "nombreJours" || e.dataField === "nombreAdultes" || e.dataField === "nombreEnfants") {
                e.editorOptions.readOnly = e.row?.data.type === ReservationDetailTypeDetail.Automatique || e.row?.data.type === ReservationDetailTypeDetail.Modifier;
            }
        }
    }, []);
    const onCellPrepared = useCallback((e: any) => {
        if (e.rowType == "data" && e.row?.data.type === ReservationDetailTypeDetail.Manuel) {
            e.cellElement.style.backgroundColor = e.row.isSelected ? "default" : "#eef7fd";
            e.cellElement.style.color = "black";
        }
        if (e.rowType == "data" && e.row?.data.type === ReservationDetailTypeDetail.Modifier) {
            e.cellElement.style.backgroundColor = e.row.isSelected ? "default" : "#fdf9ee";
            e.cellElement.style.color = "black";
        }
    }, []);
    const onRowRemoved = useCallback((e: any) => {
        e.data.type = ReservationDetailTypeDetail.Effacer;
        const rowUpdated = {...data.data, reservationDetails: [...data.data.reservationDetails, e.data]}
        dataGridStore.update(e.data.idReservationDetailSommaire, {reservationDetailSommaire: rowUpdated, detailOnly: true});
    }, [data.data, dataGridStore]);
    const filterValue = useMemo(() => ["type", "<>", ReservationDetailTypeDetail.Effacer], []);
    const editorOptionsMaskBehavior = useMemo(() => ({useMaskBehavior: true}), []);
    const adulteCellRender = useCallback((e: any) => trancheDetailAgeCellRender({...e, adulte: true}), [trancheDetailAgeCellRender]);
    const enfantCellRender = useCallback((e: any) => trancheDetailAgeCellRender({...e, adulte: false}), [trancheDetailAgeCellRender]);
    return (
        <div style={{paddingLeft: visibleSiUnite ? "0" : "45%"}}>
            <DataGrid ref={dataGridRef} dataSource={reservationDetails} columnHidingEnabled={false} allowColumnResizing={true} columnResizingMode={"nextColumn"} onInitNewRow={onInitNewRow}
                      className={"grid-detail-reservation"}
                      keyExpr={"idReservationDetail"}
                      onRowInserted={onRowInserted}
                      onRowUpdated={onRowUpdated}
                      filterValue={filterValue}
                      autoNavigateToFocusedRow={false}
                      onEditorPreparing={onEditorPreparing}
                      onCellPrepared={onCellPrepared}
                      onRowRemoved={onRowRemoved}
                      focusedRowEnabled={true}
                      customizeColumns={customizeColumns}
            >
                <Editing confirmDelete={true} allowAdding={isModeModifier} allowDeleting={true} allowUpdating={isModeModifier} newRowPosition={"first"} mode={"cell"} startEditAction={"click"}/>
                <Column caption={"Date début"} visible={visibleSiUnite} dataType={"datetime"} width={"11%"} format={"dd-MM-yyyy HH:mm"} editorOptions={editorOptionsMaskBehavior} dataField={"dateDebut"}/>
                <Column caption={"Date fin"} visible={visibleSiUnite} dataType={"datetime"} width={"11%"} format={"dd-MM-yyyy HH:mm"} dataField={"dateFin"} editorOptions={editorOptionsMaskBehavior}/>
                <Column caption={"Durée"} visible={visibleSiUnite} dataType={"number"} width={"4rem"} alignment={"center"} dataField={"nombreJours"}/>
                <Column caption={"Adulte(s"} visible={visibleSiUnite} dataType={"number"} width={"4rem"} cellRender={adulteCellRender}/>
                <Column caption={"Enfant(s)"} visible={visibleSiUnite} dataType={"number"} width={"4rem"} cellRender={enfantCellRender}/>
                <Column caption={"Plan"} visible={visibleSiUnite} dataType={"object"} width={"20%"} alignment={"center"} dataField={"idPlanEntete"}>
                    <Lookup{...planEditorOptions}/>
                </Column>
                <Column caption={"Quantité"} dataField={"quantite"} dataType={"number"}/>
                <Column caption={"Prix"} dataType="number" format={"#0.00 $"} dataField={"prixParJourAdultes"}/>
                <Column caption={"Total"} dataType="number" width={totalWidth} format={"#0.00 $"} calculateCellValue={getCalculateCellValue}/>
                <Column dataField={"type"} dataType={"string"} visible={false}/>
                <Column dataField={"hebergementTaxable"} dataType={"boolean"}/>
                <Column type="buttons" width={32}/>
            </DataGrid>
        </div>
    );
}

export default ReservationDetailView;